import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Cable, CheckCircle2, ExternalLink, KeyRound, XCircle } from "lucide-react";
import { api } from "../lib/api";
import type { Connector, Platform } from "../lib/types";
import { dateTime, platformLabel, statusLabel } from "../lib/format";
import { Modal } from "../components/Modal";
import { StatusBadge, statusTone } from "../components/StatusBadge";
import { useI18n } from "../lib/i18n";

const fieldMap: Record<Platform, Array<{ key: string; label: string; secret?: boolean }>> = {
  amazon: [
    { key: "clientId", label: "Client ID" },
    { key: "clientSecret", label: "Client Secret", secret: true },
    { key: "refreshToken", label: "Refresh Token", secret: true },
    { key: "marketplaceId", label: "Marketplace ID" }
  ],
  tiktok: [
    { key: "appKey", label: "App Key" },
    { key: "appSecret", label: "App Secret", secret: true }
  ],
  shein: [
    { key: "appId", label: "App ID" },
    { key: "openKeyId", label: "Open Key ID" },
    { key: "secretKey", label: "Secret Key", secret: true }
  ]
};

export function ConnectorsPage() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [editing, setEditing] = useState<Connector | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const query = useQuery({ queryKey: ["connectors"], queryFn: () => api.get<{ connectors: Connector[] }>("/api/connectors") });
  const save = useMutation({
    mutationFn: () => api.put(`/api/connectors/${editing?.platform}`, { enabled: true, config: values }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["connectors"] });
      setEditing(null);
      setValues({});
    }
  });
  const test = useMutation({
    mutationFn: () => api.post<{ ok: boolean; message: string }>(`/api/connectors/${editing?.platform}/test`),
    onSuccess: (result) => setTestResult(result),
    onError: (error) => setTestResult({ ok: false, message: error.message })
  });

  function openConnector(connector: Connector) {
    setEditing(connector);
    setValues({});
    setTestResult(null);
  }

  return (
    <div className="connector-page">
      <div className="connector-grid">
        {(query.data?.connectors ?? []).map((connector) => (
          <section className="panel connector-card" key={connector.platform}>
            <div className="connector-card-head">
              <div className={`platform-mark large platform-${connector.platform}`}>{connector.label.slice(0, 1)}</div>
              <div>
                <h2>{connector.label}</h2>
                <StatusBadge tone={connector.enabled ? "success" : connector.configured ? "warning" : "neutral"}>
                  {connector.enabled ? t("已连接") : connector.configured ? t("需启用") : t("未配置")}
                </StatusBadge>
              </div>
            </div>
            <p>{t(connector.description)}</p>
            <div className="capability-list">
              {connector.capabilities.map((capability) => (
                <span key={capability.key} className={capability.enabled ? "capability enabled" : "capability"}>
                  {capability.enabled ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                  {t(capability.label)}
                </span>
              ))}
            </div>
            <div className="connector-meta">
              <span>{t("店铺")} {connector.storeCount}</span>
              <span>{t("上次同步")} {dateTime(connector.lastSyncAt)}</span>
            </div>
            <div className="connector-actions">
              <a className="ghost-button" href={connector.docsUrl} target="_blank" rel="noreferrer">
                <ExternalLink size={15} />
                {t("官方文档")}
              </a>
              <button type="button" className="primary-button" onClick={() => openConnector(connector)}>
                <KeyRound size={15} />
                {t("配置凭证")}
              </button>
            </div>
          </section>
        ))}
      </div>

      <Modal open={Boolean(editing)} title={`${t("配置")} ${editing ? platformLabel(editing.platform) : ""}`} onClose={() => setEditing(null)}>
        <div className="form-stack">
          {editing ? (
            <>
              {fieldMap[editing.platform].map((field) => (
                <label className="field" key={field.key}>
                  <span>{field.label}</span>
                  <input
                    type={field.secret ? "password" : "text"}
                    value={values[field.key] ?? ""}
                    onChange={(event) => setValues({ ...values, [field.key]: event.target.value })}
                    placeholder={t("保存后不会在页面回显")}
                  />
                </label>
              ))}
              {testResult ? (
                <div className={testResult.ok ? "form-success" : "form-error"}>
                  {testResult.ok ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                  {testResult.message}
                </div>
              ) : null}
              {save.isError ? <div className="form-error">{save.error.message}</div> : null}
              <div className="modal-actions">
                <button type="button" className="ghost-button" onClick={() => test.mutate()} disabled={test.isPending}>
                  {test.isPending ? t("测试中...") : t("测试连接")}
                </button>
                <button type="button" className="primary-button" onClick={() => save.mutate()} disabled={save.isPending}>
                  {save.isPending ? t("保存中...") : t("保存并启用")}
                </button>
              </div>
            </>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
